/**
 * Whitelabel information request form — Instagram funnel landing.
 * Collects: Nom, Email, Téléphone (all required) + explicit GDPR consent.
 * On success → thank-you screen; the client also receives a whitelabel email.
 *
 * SEO: <meta name="robots" content="noindex,nofollow"> injected client-side.
 * Layout: self-contained, no Titan Infra header/footer.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useNoIndex = () => {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex,nofollow";
    document.head.appendChild(m);
    const prev = document.title;
    document.title = "Recevoir plus d’informations";
    return () => { document.head.removeChild(m); document.title = prev; };
  }, []);
};

export default function InsightRequest() {
  useNoIndex();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", consent: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.consent) {
      setErr("Vous devez confirmer votre consentement.");
      return;
    }
    setBusy(true);
    try {
      const utm = new URLSearchParams(window.location.search);
      const source = utm.get("utm_source") || utm.get("source") || "instagram";
      await axios.post(`${API}/insight/leads`, { ...form, source });
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Une erreur est survenue. Merci de réessayer.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="insight-request">
      <div className="border-b border-stone-200/70 bg-white/60 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-14 flex items-center justify-between">
          <Link to="/insight" className="inline-flex items-center gap-1 text-[11px] tracking-[0.2em] uppercase text-stone-500 hover:text-stone-900" data-testid="insight-back">
            <ArrowLeft className="w-3 h-3" /> Retour au dossier
          </Link>
          <span className="text-[10px] tracking-[0.25em] uppercase text-stone-400 hidden md:inline">Demande d’information</span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 md:px-10 py-16 md:py-24">
        {!done ? (
          <>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <p className="text-[10px] tracking-[0.35em] uppercase text-amber-700/80 mb-4">Demande d’information</p>
              <h1
                style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
                className="text-[36px] md:text-[52px] leading-[1.05] tracking-tight text-stone-900"
              >
                Recevez la brochure par email<br />
                <span className="italic text-stone-600">— puis on vous rappelle sous 48 h.</span>
              </h1>
              <p className="mt-6 text-stone-700 text-[16px] leading-relaxed" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
                Dès l’envoi du formulaire, vous recevrez par email notre <strong>dossier éditorial complet</strong> sur l’infrastructure de l’intelligence artificielle (brochure PDF). Un membre de notre équipe vous rappellera ensuite <strong>sous 48 h ouvrées</strong> — sans engagement, simplement pour répondre à vos questions.
              </p>
            </motion.div>

            <motion.form
              onSubmit={submit}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-12 space-y-6"
              data-testid="insight-form"
            >
              <label className="block">
                <span className="text-[11px] tracking-[0.25em] uppercase text-stone-500">Nom complet</span>
                <input
                  data-testid="insight-name"
                  required minLength={2} maxLength={120}
                  value={form.full_name}
                  onChange={(e) => setForm(f => ({ ...f, full_name: e.target.value }))}
                  className="mt-2 w-full h-12 border-b border-stone-300 bg-transparent text-[17px] focus:outline-none focus:border-stone-900 transition-colors"
                  style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
                  placeholder="Prénom Nom"
                />
              </label>

              <label className="block">
                <span className="text-[11px] tracking-[0.25em] uppercase text-stone-500">Email</span>
                <input
                  data-testid="insight-email"
                  required type="email" maxLength={200}
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  className="mt-2 w-full h-12 border-b border-stone-300 bg-transparent text-[17px] focus:outline-none focus:border-stone-900 transition-colors font-mono"
                  placeholder="votre@email.com"
                />
              </label>

              <label className="block">
                <span className="text-[11px] tracking-[0.25em] uppercase text-stone-500">Téléphone (obligatoire)</span>
                <input
                  data-testid="insight-phone"
                  required type="tel" minLength={6} maxLength={25}
                  value={form.phone}
                  onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                  className="mt-2 w-full h-12 border-b border-stone-300 bg-transparent text-[17px] focus:outline-none focus:border-stone-900 transition-colors font-mono"
                  placeholder="+33 6 12 34 56 78"
                />
                <span className="mt-2 text-[11px] text-stone-500 block">Nous utilisons uniquement ce numéro pour l’appel de suivi. Il n’est jamais partagé.</span>
              </label>

              <label className="flex items-start gap-3 pt-4 cursor-pointer">
                <input
                  data-testid="insight-consent"
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => setForm(f => ({ ...f, consent: e.target.checked }))}
                  className="mt-1 h-4 w-4 accent-amber-500"
                />
                <span className="text-[12px] leading-relaxed text-stone-600">
                  J’accepte d’être recontacté par téléphone et par email pour recevoir les informations que je sollicite. Je peux demander à tout moment la suppression de mes données en répondant à l’email de confirmation.
                </span>
              </label>

              {err && (
                <p data-testid="insight-error" className="text-[13px] text-red-700 bg-red-50 border border-red-200 rounded-md px-4 py-3">{err}</p>
              )}

              <div className="pt-4">
                <button
                  data-testid="insight-submit"
                  disabled={busy}
                  type="submit"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-stone-900 hover:bg-stone-800 text-white rounded-full text-[13px] tracking-[0.15em] uppercase font-medium disabled:opacity-50 transition-colors"
                >
                  {busy ? "Envoi…" : "Envoyer ma demande"}
                </button>
                <p className="mt-6 text-[11px] text-stone-400 leading-relaxed">
                  Ce formulaire est protégé contre les envois automatisés. Vos données sont traitées conformément au RGPD et ne sont utilisées que pour donner suite à votre demande.
                </p>
              </div>
            </motion.form>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
            className="text-center py-16"
            data-testid="insight-success"
          >
            <div className="w-16 h-16 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center mx-auto">
              <Check className="w-7 h-7 text-emerald-700" />
            </div>
            <h1
              style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
              className="mt-8 text-[36px] md:text-[46px] leading-[1.1] tracking-tight text-stone-900"
            >
              Merci, votre demande est bien reçue.
            </h1>
            <p className="mt-6 text-stone-700 text-[16px] leading-relaxed max-w-lg mx-auto" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
              Vous recevrez immédiatement la brochure par email, puis un membre de notre équipe vous rappellera <strong>sous 48 h ouvrées</strong> au numéro que vous avez indiqué.
            </p>
            <p className="mt-10 text-[11px] tracking-[0.2em] uppercase text-stone-400">
              Vous pouvez fermer cette page.
            </p>
          </motion.div>
        )}
      </div>

      <footer className="bg-white border-t border-stone-200 mt-16">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-8">
          <p className="text-[11px] text-stone-500 leading-relaxed max-w-3xl">
            Formulaire à visée éditoriale. La transmission de vos coordonnées ne constitue ni une souscription, ni un engagement financier. Vos données sont conservées pour une durée maximale de 24 mois et vous pouvez en demander la suppression à tout moment.
          </p>
        </div>
      </footer>
    </div>
  );
}
